import { Component, computed, signal, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { ThemeToggleComponent } from './shared/ui';
import { AssistantPanelComponent } from './shared/assistant/assistant-panel.component';
import { LogixOrbComponent } from './shared/assistant/logix-orb.component';
import { NotificationBellComponent } from './shared/notifications/notification-bell.component';
import { AuthService } from './core/auth/auth.service';

interface NavLeaf {
  label: string;
  path: string;
}

interface NavGroup {
  label: string;
  key: string;
  children: NavLeaf[];
  /** Rec-3: role gate — 'employee' shows only when the login is bound to an employee;
   *  'manager' shows only for manager logins. Undefined = always visible. */
  gate?: 'employee' | 'manager';
}

interface NavSection {
  label: string;
  key: string;
  /** Heroicons-outline SVG path data, rendered at 18x18 inside a tinted icon chip */
  icon: string;
  /** Tailwind text-color class for the icon itself, e.g. 'text-info' */
  iconColor: string;
  /** Tailwind bg-color class for the icon's chip background, e.g. 'bg-info-50' */
  iconBg: string;
  groups: NavGroup[];
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, ThemeToggleComponent, AssistantPanelComponent, LogixOrbComponent, NotificationBellComponent],
  templateUrl: './app.component.html'
})
export class AppComponent {
  title = 'Logix AI ERP';

  /** Live module search — typing filters the 60+ screens instantly. */
  navQuery = signal('');

  today = new Date();

  /** Groups the current user may see (Rec-3 role gate: ESS needs a linked employee,
   *  MSS needs a manager login). Applied before the live search filter. */
  private gateOk(g: NavGroup): boolean {
    if (g.gate === 'employee') return this.auth.isEmployee();
    if (g.gate === 'manager') return this.auth.isManager();
    return true;
  }

  /** Sections filtered by role gate + the live search. Empty query returns everything
   *  the user is permitted to see. */
  visibleSections = computed<NavSection[]>(() => {
    const q = this.navQuery().trim().toLowerCase();
    return this.sections
      // User Setup: department / role-level section gating (Super Admin / CEO / Administrator
      // see everything; a Dept User / HOD sees only their department's sections).
      .filter(s => this.auth.canSeeSection(s.key))
      .map(s => ({
        ...s,
        groups: s.groups
          .filter(g => this.gateOk(g))
          .map(g => ({ ...g, children: q ? g.children.filter(c => c.label.toLowerCase().includes(q)) : g.children }))
          .filter(g => g.children.length > 0)
      }))
      .filter(s => s.groups.length > 0);
  });

  searching = computed(() => this.navQuery().trim().length > 0);

  // No section is expanded on load — the Dashboard is the landing view and the
  // whole sidebar starts collapsed. A section auto-expands only when you navigate
  // into one of its modules (see revealActive), never by default.
  expandedSections = signal<Set<string>>(new Set());
  expandedGroups = signal<Set<string>>(new Set());

  /** Breadcrumb trail for the current route (e.g. Finance › Accounts › Books Records Setup),
   *  giving orientation in the deep 3-level navigation. Recomputed on every NavigationEnd. */
  crumbs = signal<string[]>(['Dashboard']);

  /** The current page name (last crumb) — shown as the header's bold title line. */
  pageTitle = computed(() => { const c = this.crumbs(); return c[c.length - 1] || 'Dashboard'; });
  /** The ancestor trail (all crumbs above the page) — shown as a muted breadcrumb subtitle. */
  trail = computed(() => this.crumbs().slice(0, -1));

  // ---- Command palette (Ctrl / Cmd + K): fuzzy-jump to any module ----
  @ViewChild('paletteInput') paletteInput?: ElementRef<HTMLInputElement>;
  paletteOpen = signal(false);
  paletteQuery = signal('');
  paletteIndex = signal(0);

  /** Every navigable leaf flattened to a searchable row (respects role gates). */
  private flatNav = computed(() => {
    const out: { section: string; group: string; label: string; path: string }[] = [];
    for (const s of this.sections) {
      if (!this.auth.canSeeSection(s.key)) continue;
      for (const g of s.groups)
        if (this.gateOk(g))
          for (const c of g.children)
            out.push({ section: s.label, group: g.label, label: c.label, path: c.path });
    }
    return out;
  });
  paletteResults = computed(() => {
    const q = this.paletteQuery().trim().toLowerCase();
    const all = this.flatNav();
    if (!q) return all.slice(0, 60);
    return all.filter(i => i.label.toLowerCase().includes(q) || i.section.toLowerCase().includes(q) || i.group.toLowerCase().includes(q)).slice(0, 60);
  });

  openPalette(): void {
    this.paletteQuery.set(''); this.paletteIndex.set(0); this.paletteOpen.set(true);
    setTimeout(() => this.paletteInput?.nativeElement.focus(), 0);
  }
  closePalette(): void { this.paletteOpen.set(false); }
  onPaletteQuery(v: string): void { this.paletteQuery.set(v); this.paletteIndex.set(0); }
  paletteGo(path: string): void { this.closePalette(); this.router.navigateByUrl(path); }

  // ---- Favorites (starred modules, persisted in localStorage) ----
  favorites = signal<string[]>(this.loadFavorites());
  private loadFavorites(): string[] {
    try { return JSON.parse(localStorage.getItem('logix_favorites') || '[]'); } catch { return []; }
  }
  isFavorite(path: string): boolean { return this.favorites().includes(path); }
  toggleFavorite(path: string, e?: Event): void {
    e?.stopPropagation(); e?.preventDefault();
    const next = this.isFavorite(path) ? this.favorites().filter(p => p !== path) : [...this.favorites(), path];
    this.favorites.set(next);
    localStorage.setItem('logix_favorites', JSON.stringify(next));
  }
  favoriteItems = computed(() => {
    const favs = this.favorites();
    return this.flatNav().filter(i => favs.includes(i.path));
  });

  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
      if (!this.auth.isLoggedIn()) return;
      e.preventDefault(); this.paletteOpen() ? this.closePalette() : this.openPalette(); return;
    }
    if (!this.paletteOpen()) return;
    const results = this.paletteResults();
    if (e.key === 'Escape') { e.preventDefault(); this.closePalette(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); this.paletteIndex.set(Math.min(this.paletteIndex() + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.paletteIndex.set(Math.max(this.paletteIndex() - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const r = results[this.paletteIndex()]; if (r) this.paletteGo(r.path); }
  }

  constructor(private router: Router, public auth: AuthService) {
    // Keep the left sidebar in sync with the active route — so when the user
    // navigates (including via an AI Assistant deep-link), the section + group
    // that contains the open module auto-expand and reveal the active item.
    this.router.events.subscribe(e => {
      if (e instanceof NavigationEnd) { this.revealActive(e.urlAfterRedirects); this.computeCrumbs(e.urlAfterRedirects); }
    });
  }

  /** Sign out and return to the login screen. */
  logout(): void {
    this.auth.logout();
  }

  /** Two-letter avatar initials for the signed-in user. */
  userInitials(): string {
    const u = this.auth.currentUser();
    const name = (u?.fullName || u?.userName || '').trim();
    if (!name) return 'U';
    const parts = name.split(/\s+/);
    return (parts[0][0] + (parts.length > 1 ? parts[1][0] : '')).toUpperCase();
  }

  /** Expand the section + group that own the given URL so the module shows in the nav. */
  private revealActive(url: string): void {
    const clean = url.split('?')[0].split('#')[0];
    for (const s of this.sections) {
      for (const g of s.groups) {
        if (g.children.some(c => clean === c.path || clean.startsWith(c.path + '/'))) {
          const secs = new Set(this.expandedSections()); secs.add(s.key); this.expandedSections.set(secs);
          const grps = new Set(this.expandedGroups()); grps.add(g.key); this.expandedGroups.set(grps);
          return;
        }
      }
    }
  }

  /** Build the breadcrumb trail (Section › Group › Page) for a URL. Dashboard is the
   *  landing page and shows a single "Dashboard" crumb. */
  private computeCrumbs(url: string): void {
    const clean = url.split('?')[0].split('#')[0];
    if (clean === '/' || clean === '') { this.crumbs.set(['Dashboard']); return; }
    for (const s of this.sections) {
      for (const g of s.groups) {
        const leaf = g.children.find(c => clean === c.path || clean.startsWith(c.path + '/'));
        if (leaf) { this.crumbs.set([s.label, g.label, leaf.label]); return; }
      }
    }
    this.crumbs.set(['Logix AI ERP']);
  }

  toggleSection(key: string): void {
    const next = new Set(this.expandedSections());
    next.has(key) ? next.delete(key) : next.add(key);
    this.expandedSections.set(next);
  }

  toggleGroup(key: string): void {
    const next = new Set(this.expandedGroups());
    next.has(key) ? next.delete(key) : next.add(key);
    this.expandedGroups.set(next);
  }

  isSectionOpen(key: string): boolean {
    // During a live search every matching section stays open so results are visible.
    return this.searching() || this.expandedSections().has(key);
  }

  isGroupOpen(key: string): boolean {
    return this.searching() || this.expandedGroups().has(key);
  }

  // ============================================================
  // Main menus, in Sir Rashid's requested order: Desktop (direct
  // link, rendered separately in the template) → Configuration →
  // Finance → Finance Reports → Transaction PSS (last).
  //
  // Each section carries its own icon chip color so the sidebar
  // reads at a glance: Configuration = blue (system/settings),
  // Finance = green (money), Finance Reports = violet (analytics),
  // Transaction PSS = amber (day-to-day operations/warehouse).
  // ============================================================
  sections: NavSection[] = [
    {
      label: 'Configuration',
      key: 'configuration',
      icon: 'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28z',
      iconColor: 'text-info',
      iconBg: 'bg-info-50',
      groups: [
        {
          label: 'Company', key: 'configuration-company', children: [
            { label: 'Email Settings', path: '/configuration/company/email' },
            { label: 'Fiscal Span', path: '/configuration/company/fiscal-span' },
            { label: 'Profile', path: '/configuration/company/profile' },
            { label: 'Settings', path: '/configuration/company/settings' }
          ]
        },
        {
          label: 'Management', key: 'configuration-management', children: [
            { label: 'Currency', path: '/configuration/management/currency' },
            { label: 'Naming Conventions', path: '/configuration/management/naming-conventions' }
            // { label: 'Pending Approvals', path: '/configuration/management/pending-approvals' } // HIDDEN from menu (legacy reference screen). Files + route intact — un-comment to restore.
          ]
        },
        {
          label: 'Tax Setup', key: 'configuration-tax-setup', children: [
            { label: 'Taxes', path: '/configuration/tax-setup/taxes' },
            { label: 'Tax Rate History', path: '/configuration/tax-setup/tax-rate-history' },
            { label: 'Tax Authorities', path: '/configuration/tax-setup/tax-authorities' },
            { label: 'Withholding Tax', path: '/configuration/tax-setup/withholding-tax' },
            { label: 'Tax Group', path: '/configuration/tax-setup/tax-group' },
            { label: 'Tax Register', path: '/configuration/tax-setup/tax-register' },
            { label: 'FBR Settings', path: '/configuration/tax-setup/fbr-settings' },
            { label: 'Tax Exemptions', path: '/configuration/tax-setup/tax-exemptions' },
            { label: 'FBR Returns & Tax Reports', path: '/configuration/tax-setup/fbr-returns' }
          ]
        }
      ]
    },
    // Finance — ALL 8 sections wired (2026-07-18, Sir Rashid's go-ahead).
    // Banking's backend is live; Earnings/Expenses/Payments/Requisition
    // Status/Setting screens point at their canonical service routes and
    // come fully online as each backend section is enabled (csproj).
    {
      label: 'Finance',
      key: 'finance',
      icon: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.625c.621 0 1.125.504 1.125 1.125v.375m-18 0V18a2.25 2.25 0 002.25 2.25h13.5A2.25 2.25 0 0020.25 18V6.375m-18 0h18M9 12a3 3 0 11-6 0 3 3 0 016 0z',
      iconColor: 'text-success',
      iconBg: 'bg-success-50',
      groups: [
        {
          label: 'Accounts', key: 'finance-accounts', children: [
            { label: 'Books Records Setup', path: '/finance/accounts/books-records-setup' },
            { label: 'Chart of Accounts', path: '/finance/accounts/chart-of-accounts' },
            { label: 'Startup Equity', path: '/finance/accounts/startup-equity' }
          ]
        },
        {
          label: 'Transactions', key: 'finance-transactions', children: [
            { label: 'Funds Transfer', path: '/finance/transactions/funds-transfer' },
            { label: 'Funds Transfer Approval', path: '/finance/transactions/funds-transfer-approval' },
            { label: 'General Records', path: '/finance/transactions/general-records' },
            { label: 'Vouchers', path: '/finance/transactions/vouchers' }
          ]
        },
        {
          label: 'Payments', key: 'finance-payments', children: [
            { label: 'Cash & Bank Payments', path: '/finance/payments/cash-and-bank-payments' }
          ]
        },
        {
          label: 'Earnings', key: 'finance-earnings', children: [
            { label: 'Cash & Bank Receipts Voucher', path: '/finance/earnings/cash-and-bank-receipts-voucher' }
          ]
        },
        {
          label: 'Expenses', key: 'finance-expenses', children: [
            { label: 'Expense Type', path: '/finance/expenses/expense-type' },
            // HIDDEN 2026-08-15 (audit A2) — Expense Setup backend is the parked legacy shell (404). Un-hide when built.
            // { label: 'Expense Setup', path: '/finance/expenses/expense-setup' },
            { label: 'Expenses', path: '/finance/expenses/expenses' },
            { label: 'Expenses Approval', path: '/finance/expenses/expenses-approval' },
            { label: 'Expense Payments', path: '/finance/expenses/expense-payments' }
          ]
        },
        {
          label: 'Requisition Status', key: 'finance-requisition-status', children: [
            { label: 'Credit Notes', path: '/finance/requisition-status/credit-notes' },
            { label: 'Debit Notes', path: '/finance/requisition-status/debit-notes' }
          ]
        },
        {
          label: 'Banking', key: 'finance-banking', children: [
            { label: 'Cheque Book Configuration', path: '/finance/banking/cheque-book-configuration' },
            { label: 'Cheque Register', path: '/finance/banking/cheque-register' },
            { label: 'Cheque Deposit', path: '/finance/banking/cheque-deposit' },
            { label: 'Cheque Clear', path: '/finance/banking/cheque-clear' },
            { label: 'Cheque Bounce', path: '/finance/banking/cheque-bounce' },
            { label: 'Cheques Return', path: '/finance/banking/cheques-return' },
            { label: 'Cheques Dashboard', path: '/finance/banking/cheques-dashboard' }
          ]
        },
        {
          label: 'Setting', key: 'finance-setting', children: [
            // HIDDEN 2026-08-15 (audit A2) — these 5 backends are PARKED (DbSets commented,
            // controllers not compiled -> 404). Routes + files intact; un-hide when built.
            // { label: 'Code Formats', path: '/finance/setting/code-formats' },
            // { label: 'Cost Center', path: '/finance/setting/cost-center' },
            // { label: 'COA Step Code', path: '/finance/setting/coa-step-code' },
            // { label: 'Cost Centers Groups', path: '/finance/setting/cost-centers-groups' },
            // { label: 'Balance Sheet Family', path: '/finance/setting/balance-sheet-family' },
            { label: 'GL Account Determination', path: '/finance/setting/gl-account-determination' },
            { label: 'GL Backfill', path: '/finance/setting/gl-backfill' }
          ]
        }
      ]
    },
    {
      label: 'Finance Reports',
      key: 'finance-reports',
      icon: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
      iconColor: 'text-violet-600',
      iconBg: 'bg-violet-50',
      groups: [
        {
          label: 'Audit Reports', key: 'finance-reports-audit-reports', children: [
            { label: 'General Records', path: '/finance-reports/audit-reports/general-records' },
            { label: 'Trial Balance', path: '/finance-reports/audit-reports/trial-balance' }
          ]
        },
        /* HIDDEN 2026-08-15 (audit A2/C) — these reports run (ReportEngine) but can NEVER
           have data: the Cost Center master + Cost Centers Groups are PARKED, so no document
           carries a cost-center code. Un-hide when Cost Center accounting is built (Phase 2/3).
        {
          label: 'Cost Centers Group Reports', key: 'finance-reports-ccgr', children: [
            { label: 'Budget Monthly Analysis', path: '/finance-reports/cost-centers-group-reports/budget-monthly-analysis' },
            { label: 'Cost Center Account Summary', path: '/finance-reports/cost-centers-group-reports/cost-center-account-summary' },
            { label: 'Cost Centers Group Aging', path: '/finance-reports/cost-centers-group-reports/cost-centers-group-aging' },
            { label: 'Cost Centers Group Monthly Analysis', path: '/finance-reports/cost-centers-group-reports/cost-centers-group-monthly-analysis' },
            { label: 'Cost Centers Groups Ledger', path: '/finance-reports/cost-centers-group-reports/cost-centers-groups-ledger' }
          ]
        },
        */
        {
          label: 'Generic Statements', key: 'finance-reports-generic-statements', children: [
            { label: 'Accounts Ledger', path: '/finance-reports/generic-statements/accounts-ledger' },
            { label: 'Aged Accounts', path: '/finance-reports/generic-statements/aged-accounts' },
            { label: 'Aged Payables', path: '/finance-reports/generic-statements/aged-payables' },
            { label: 'Aged Receivable', path: '/finance-reports/generic-statements/aged-receivable' },
            { label: 'Balance Sheet', path: '/finance-reports/generic-statements/balance-sheet' },
            { label: 'Incoming Payments Log', path: '/finance-reports/generic-statements/incoming-payments-log' },
            { label: 'Outgoing Payments Log', path: '/finance-reports/generic-statements/outgoing-payments-log' },
            { label: 'Partner Statement of Account', path: '/finance-reports/generic-statements/partner-statement-of-account' },
            { label: 'Profit & Loss Accounts', path: '/finance-reports/generic-statements/profit-and-loss-accounts' },
            { label: 'Statement of Accounts', path: '/finance-reports/generic-statements/statement-of-accounts' },
            { label: 'Summary of Main Accounts', path: '/finance-reports/generic-statements/summary-of-main-accounts' }
          ]
        },
        {
          label: 'Summary', key: 'finance-reports-summary', children: [
            { label: 'Executive Summary', path: '/finance-reports/summary/executive-summary' },
            { label: 'Journal Entries Summary', path: '/finance-reports/summary/journal-entries-summary' }
          ]
        }
      ]
    },
    {
      label: 'Transaction · Purchase Sales Stock',
      key: 'transaction',
      icon: 'M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007z',
      iconColor: 'text-warning',
      iconBg: 'bg-warning-50',
      groups: [
        {
          label: 'Core', key: 'transaction-core', children: [
            { label: 'Units of Measurement', path: '/pss/core/uom' },
            { label: 'UOM Group', path: '/pss/core/uom-group' },
            { label: 'Customer Group', path: '/pss/core/customer-group' },
            { label: 'Payment Term', path: '/pss/core/payment-term' },
            { label: 'Price List', path: '/pss/core/price-list' },
            { label: 'Product Attributes', path: '/pss/core/product-attributes' },
            { label: 'Product Company', path: '/pss/core/product-company' },
            { label: 'Projects', path: '/pss/core/projects' },
            { label: 'Warehouse', path: '/pss/core/warehouse' },
            { label: 'Sales Region', path: '/pss/core/sales-region' },
            { label: 'Commission Setup', path: '/pss/core/commission-setup' },
            { label: 'Locations', path: '/pss/core/locations' },
            { label: 'Shipments', path: '/pss/core/shipments' }
          ]
        },
        {
          label: 'Product Management', key: 'transaction-product-management', children: [
            { label: 'Products', path: '/pss/product-management/products' },
            { label: 'Product Relocation', path: '/pss/product-management/product-relocation' },
            { label: 'Product In-house Use', path: '/pss/product-management/product-in-house-use' },
            { label: 'Product Reconciliation', path: '/pss/product-management/product-reconciliation' },
            { label: 'Product Damage', path: '/pss/product-management/product-damage' },
            { label: 'Product Opening Stock', path: '/pss/product-management/product-opening-stock' },
            { label: 'Product Categories', path: '/pss/product-management/product-categories' },
            { label: 'Add On Group', path: '/pss/product-management/add-on-group' },
            { label: 'Category Ingredient', path: '/pss/product-management/category-ingredient' },
            { label: 'Product Taxation', path: '/pss/product-management/product-taxation' },
            { label: 'Costing & Estimation | Recipe', path: '/pss/product-management/costing-estimation-recipe' },
            { label: 'Product Updation', path: '/pss/product-management/product-updation' }
          ]
        },
        {
          label: 'Setting', key: 'transaction-setting', children: [
            { label: 'Category / Product Step Code', path: '/pss/setting/category-step-code' },
            { label: 'Warehouse Step Code', path: '/pss/setting/warehouse-step-code' },
            { label: 'Channel Prices', path: '/pss/setting/channel-prices' },
            { label: 'Payment Method', path: '/pss/setting/payment-method' },
            { label: 'Code Formats', path: '/pss/setting/code-formats' },
            // HIDDEN 2026-08-15 (audit A1) — DEAD posting/mapping config: PSS.GlobalSetups
            // groups 101/111/121/211 are read by NOTHING. The real, engine-consumed GL
            // mapping lives at Finance > Setting > GL Account Determination (Finance.PostingRules).
            // { label: 'Procurement Settings', path: '/pss/setting/procurement-settings' },
            // { label: 'Procurement Posting', path: '/pss/setting/procurement-posting' },
            { label: 'Procurement Report Setup', path: '/pss/setting/procurement-report-setup' },
            // { label: 'Purchase Account Mapping', path: '/pss/setting/purchase-account-mapping' },
            // { label: 'Sales Account Mapping', path: '/pss/setting/sales-account-mapping' },
            { label: 'Customer Credit Recharge', path: '/pss/setting/customer-credit-recharge' }
          ]
        },
        {
          label: 'Labeling', key: 'transaction-labeling', children: [
            { label: 'Label Designing', path: '/pss/labeling/label-designing' },
            { label: 'Label Printing', path: '/pss/labeling/label-printing' }
          ]
        },
        {
          label: 'Tracking', key: 'transaction-tracking', children: [
            { label: 'Orders Tracking', path: '/pss/tracking/orders-tracking' },
            { label: 'Vendor Directory', path: '/pss/tracking/vendor-directory' },
            { label: 'Customer Directory', path: '/pss/tracking/customer-directory' },
            { label: 'Good Delivery Note Directory', path: '/pss/tracking/good-delivery-note-directory' },
            { label: 'Goods Received Receipt Directory', path: '/pss/tracking/goods-received-receipt-directory' }
          ]
        },
        {
          label: 'Loyalty Management', key: 'transaction-loyalty-management', children: [
            { label: 'Loyalty', path: '/pss/loyalty-management/loyalty' },
            { label: 'Loyalty Card', path: '/pss/loyalty-management/loyalty-card' },
            { label: 'Loyalty History', path: '/pss/loyalty-management/loyalty-history' },
            { label: 'History Report', path: '/pss/loyalty-management/history-report' }
          ]
        },
        {
          label: 'Import Export', key: 'transaction-import-export', children: [
            { label: 'Letter of Credit', path: '/pss/import-export/letter-of-credit' },
            { label: 'Customs & Trade Expenses', path: '/pss/import-export/customs-trade-expenses' },
            { label: 'Performa Invoice', path: '/pss/import-export/performa-invoice' },
            { label: 'Import Costing & Valuation', path: '/pss/import-export/import-costing-valuation' }
          ]
        },
        {
          label: 'Business Partners', key: 'transaction-business-partners', children: [
            { label: 'Vendors', path: '/pss/business-partners/vendors' },
            { label: 'Customers', path: '/pss/business-partners/customers' },
            { label: 'SMS / WhatsApp', path: '/pss/business-partners/sms-whatsapp' },
            { label: 'Sales Agent', path: '/pss/business-partners/sales-agent' },
            { label: 'Collection Agent Policy at recovery %', path: '/pss/business-partners/collection-agent' }
          ]
        },
        {
          label: 'Purchasing-AP', key: 'transaction-purchasing-ap', children: [
            { label: 'Purchase Requisition', path: '/pss/purchasing-ap/purchase-requisition' },
            { label: 'Purchase Orders', path: '/pss/purchasing-ap/purchase-orders' },
            { label: 'Auto Purchase Order', path: '/pss/purchasing-ap/auto-purchase-order' },
            { label: 'Goods Received Note', path: '/pss/purchasing-ap/goods-received-note' },
            { label: 'Purchase Bill', path: '/pss/purchasing-ap/purchase-bill' },
            { label: 'Payments on Credit Bill', path: '/pss/purchasing-ap/payments-on-credit-bill' },
            { label: 'Purchase Return', path: '/pss/purchasing-ap/purchase-return' },
            { label: 'Inward Gate Pass', path: '/pss/purchasing-ap/inward-gate-pass' },
            { label: 'Outward Gate Pass', path: '/pss/purchasing-ap/outward-gate-pass' }
          ]
        },
        {
          label: 'Sales-AR', key: 'transaction-sales-ar', children: [
            { label: 'Sales Quotation', path: '/pss/sales-ar/sales-quotation' },
            { label: 'Work Orders', path: '/pss/sales-ar/work-orders' },
            { label: 'Good Delivery Note', path: '/pss/sales-ar/good-delivery-note' },
            { label: 'Sales Invoice', path: '/pss/sales-ar/sales-invoice' },
            { label: 'Sales Refunds', path: '/pss/sales-ar/sales-refunds' },
            { label: 'Inward Gate Pass', path: '/pss/sales-ar/inward-gate-pass' },
            { label: 'Outward Gate Pass', path: '/pss/sales-ar/outward-gate-pass' },
            { label: 'Collection on Credit Bill', path: '/pss/sales-ar/collection-on-credit-bill' },
            { label: 'Sales Agent Dashboard', path: '/pss/sales-ar/sales-agent-dashboard' }
          ]
        }
      ]
    },
    {
      label: 'Reports · Purchase - Sales & Stock',
      key: 'reports',
      icon: 'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z',
      iconColor: 'text-info',
      iconBg: 'bg-info-50',
      groups: [
        {
          label: 'Core', key: 'reports-core', children: [
            { label: 'Procurement Records', path: '/reports/core/procurement-records' },
            { label: 'Vendors Log', path: '/reports/core/vendors-log' },
            { label: 'Customers Report', path: '/reports/core/customers-report' },
            { label: 'Sales Agent Log', path: '/reports/core/sales-agent-log' },
            { label: 'Product Tax Log', path: '/reports/core/product-tax-log' }
          ]
        },
        {
          label: 'Estimation', key: 'reports-estimation', children: [
            { label: 'Sales Estimation Log', path: '/reports/estimation/sales-estimation-log' },
            { label: 'Purchase Requisition Log', path: '/reports/estimation/purchase-requisition-log' },
            { label: 'Purchase Requisition Product Log', path: '/reports/estimation/purchase-requisition-product-log' },
            { label: 'Purchase Requisition Unsettled Log', path: '/reports/estimation/purchase-requisition-unsettled-log' }
          ]
        },
        {
          label: 'Orders', key: 'reports-orders', children: [
            { label: 'Sales Order Log', path: '/reports/orders/sales-order-log' },
            { label: 'Sales Order Product Log', path: '/reports/orders/sales-order-product-log' },
            { label: 'Auto Purchase Order Product Log', path: '/reports/orders/auto-purchase-order-product-log' },
            { label: 'Purchase Order Log', path: '/reports/orders/purchase-order-log' }
          ]
        },
        {
          label: 'Invoice', key: 'reports-invoice', children: [
            { label: 'Sales Bill Log', path: '/reports/invoice/sales-bill-log' },
            { label: 'Sales Bill Product Log', path: '/reports/invoice/sales-bill-product-log' },
            { label: 'Sales Products Statistics', path: '/reports/invoice/sales-products-statistics' },
            { label: 'Sales Bill Product Batch Log', path: '/reports/invoice/sales-bill-product-batch-log' },
            { label: 'Purchase Bill Log', path: '/reports/invoice/purchase-bill-log' },
            { label: 'Purchase Bill Product Log', path: '/reports/invoice/purchase-bill-product-log' },
            { label: 'Sales Product Groups Statistics', path: '/reports/invoice/sales-product-groups-statistics' },
            { label: 'Norcotic Product Bill Log', path: '/reports/invoice/norcotic-product-bill-log' },
            { label: 'Good Delivery Note Log', path: '/reports/invoice/good-delivery-note-log' },
            { label: 'GDN Products Listing', path: '/reports/invoice/gdn-products-listing' },
            { label: 'Goods Received Receipt Log', path: '/reports/invoice/goods-received-receipt-log' },
            { label: 'GRR Products Listing', path: '/reports/invoice/grr-products-listing' }
          ]
        },
        {
          label: 'Refunds', key: 'reports-refunds', children: [
            { label: 'Sales Refund Log', path: '/reports/refunds/sales-refund-log' },
            { label: 'Purchase Refund Log', path: '/reports/refunds/purchase-refund-log' }
          ]
        },
        {
          label: 'Payments', key: 'reports-payments', children: [
            { label: 'Sales Collection Log', path: '/reports/payments/sales-collection-log' },
            { label: 'Purchase Payment Log', path: '/reports/payments/purchase-payment-log' },
            { label: 'Credit Sales Due Report', path: '/reports/payments/credit-sales-due-report' }
          ]
        },
        {
          label: 'Products', key: 'reports-products', children: [
            { label: 'Current Stock With Multiple UOM', path: '/reports/products/current-stock-multiple-uom' },
            { label: 'Product Stock Keeping', path: '/reports/products/product-stock-keeping' },
            { label: 'Product Stock Ledger', path: '/reports/products/product-stock-ledger' },
            { label: 'Product Stock As on Date', path: '/reports/products/product-stock-as-on-date' },
            { label: 'Products Opening Stock', path: '/reports/products/products-opening-stock' },
            { label: 'Stock Info', path: '/reports/products/stock-info' },
            { label: 'Product Damage Log', path: '/reports/products/product-damage-log' },
            { label: 'Product Stock In-house Use Log', path: '/reports/products/product-stock-in-house-use-log' },
            { label: 'Product Stock Relocation Log', path: '/reports/products/product-stock-relocation-log' },
            { label: 'Products Detail By Category', path: '/reports/products/products-detail-by-category' },
            { label: 'Stock with min and max quantity', path: '/reports/products/stock-min-max-quantity' },
            { label: 'Unsold Products', path: '/reports/products/unsold-products' },
            { label: 'Product Card Stock Batch', path: '/reports/products/product-card-stock-batch' },
            { label: 'Batch Expire Report', path: '/reports/products/batch-expire-report' }
          ]
        },
        {
          label: 'VAT / TAX Report', key: 'reports-vat', children: [
            { label: 'Tax Claim Report', path: '/reports/vat/tax-claim-report' },
            { label: 'Collective Tax', path: '/reports/vat/collective-tax' }
          ]
        }
      ]
    },
    {
      label: 'Production',
      key: 'production',
      icon: 'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z',
      iconColor: 'text-brand',
      iconBg: 'bg-brand/10',
      groups: [
        {
          label: 'Work Center', key: 'production-work-center', children: [
            { label: 'Work Center', path: '/production/work-center' },
            { label: 'Routing', path: '/production/routing' }
          ]
        },
        {
          label: 'Production', key: 'production-production', children: [
            { label: 'Bill of Material (BOM)', path: '/production/bom' },
            { label: 'Production Order', path: '/production/production-order' },
            { label: 'Production Dashboard', path: '/production/production-dashboard' },
            { label: 'Job Card', path: '/production/job-card' }
          ]
        },
        {
          label: 'Material Management', key: 'production-material-management', children: [
            { label: 'Material Receipt', path: '/production/material-receipt' },
            { label: 'Material Receipt Refund', path: '/production/material-receipt-refund' },
            { label: 'Material Damage', path: '/production/material-damage' }
          ]
        },
        {
          label: 'Finished Goods', key: 'production-finished-goods', children: [
            { label: 'Production Receipt', path: '/production/production-receipt' }
          ]
        },
        {
          label: 'Resource Management', key: 'production-resource-management', children: [
            { label: 'Resource UOM', path: '/production/resource-uom' },
            { label: 'Resource Master Data', path: '/production/resource-master' }
          ]
        },
        {
          label: 'Scrap & By-Product Management', key: 'production-scrap', children: [
            { label: 'Scrap & By-Product Management', path: '/production/scrap-management' }
          ]
        },
        {
          label: 'Production Processing', key: 'production-processing', children: [
            { label: 'Production Processing', path: '/production/production-processing' }
          ]
        },
        {
          label: 'Setting', key: 'production-setting-group', children: [
            { label: 'Code Formats', path: '/production/setting/code-formats' },
            // HIDDEN 2026-08-15 (audit A1) — DEAD: Production posts NOTHING to the GL
            // (no posting engine call anywhere in Production), so this GL-account map
            // (PROD.ProductionSettings group 11) has no consumer. Un-hide if Production->GL is built (Phase 3).
            // { label: 'Production Posting Setup', path: '/production/setting/production-posting-setup' },
            { label: 'Production Setting', path: '/production/setting/production-setting' }
          ]
        },
        {
          label: 'Packing Industry', key: 'production-packing-industry', children: [
            { label: 'Production Types', path: '/production/production-types' }
          ]
        },
        {
          label: 'Production Reports', key: 'production-reports', children: [
            { label: 'Finished Products Report', path: '/production/reports/finished-products' },
            { label: 'Work Centers Report', path: '/production/reports/work-centers' },
            { label: 'Raw Material Report', path: '/production/reports/raw-material' },
            { label: 'Raw Material Refund Report', path: '/production/reports/raw-material-refund' },
            { label: 'Production Processing Report', path: '/production/reports/production-processing' },
            { label: 'Scrap / By-Product Report', path: '/production/reports/scrap-byproduct' },
            { label: 'Resources Report', path: '/production/reports/resources' },
            { label: 'Damage Products Report', path: '/production/reports/damage-products' },
            { label: 'Production Order Report', path: '/production/reports/production-order' }
          ]
        }
      ]
    },
    {
      label: 'Quality Control',
      key: 'quality-control',
      icon: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      iconColor: 'text-info',
      iconBg: 'bg-info/10',
      groups: [
        {
          label: 'Quality Control', key: 'qc-quality-control', children: [
            { label: 'QC Criteria', path: '/quality-control/qc-criteria' },
            { label: 'Inspection Request', path: '/quality-control/inspection-request' },
            { label: 'Sample Collection', path: '/quality-control/sample-collection' },
            { label: 'Quality Inspection Results', path: '/quality-control/inspection-results' }
          ]
        },
        {
          label: 'QC Reports', key: 'qc-reports', children: [
            { label: 'Product QC Criteria Report', path: '/quality-control/reports/product-qc-criteria' },
            { label: 'Pending QC Request Report', path: '/quality-control/reports/pending-qc-request' },
            { label: 'QC Sample Usage Summary', path: '/quality-control/reports/qc-sample-usage' },
            { label: 'Inspection Result Report', path: '/quality-control/reports/inspection-result' }
          ]
        }
      ]
    },
    {
      label: 'Asset Management',
      key: 'asset-management',
      icon: 'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21',
      iconColor: 'text-warning',
      iconBg: 'bg-warning/10',
      groups: [
        {
          label: 'Configurations', key: 'asset-configurations', children: [
            { label: 'Asset Registration', path: '/asset-management/asset-registration' },
            { label: 'Asset Specifications', path: '/asset-management/asset-specifications' }
          ]
        },
        {
          label: 'Transactions', key: 'asset-transactions', children: [
            { label: 'Asset Movement', path: '/asset-management/asset-movement' },
            { label: 'Asset Depreciation', path: '/asset-management/asset-depreciation' },
            { label: 'Asset Appreciation', path: '/asset-management/asset-appreciation' }
          ]
        },
        {
          label: 'Asset Reports', key: 'asset-reports', children: [
            { label: 'Asset Registration Report', path: '/asset-management/reports/registration' },
            { label: 'Asset Movement Report', path: '/asset-management/reports/movement' },
            { label: 'Appreciation Report', path: '/asset-management/reports/appreciation' },
            { label: 'Asset Depreciation Report', path: '/asset-management/reports/depreciation' }
          ]
        }
      ]
    },
    {
      label: 'Human Resource Management',
      key: 'hrm',
      icon: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
      iconColor: 'text-brand',
      iconBg: 'bg-brand/10',
      groups: [
        {
          label: 'Configurations', key: 'hrm-configurations', children: [
            { label: 'Nationalities', path: '/hrm/nationalities' },
            { label: 'Religions', path: '/hrm/religions' },
            { label: 'Locations', path: '/hrm/locations' },
            { label: 'Branches', path: '/hrm/branches' },
            { label: 'Departments', path: '/hrm/departments' },
            { label: 'Designations', path: '/hrm/designations' },
            { label: 'Grades', path: '/hrm/grades' },
            { label: 'Asset Types', path: '/hrm/asset-types' },
            { label: 'Shift Schedules', path: '/hrm/shift-schedules' }
          ]
        },
        {
          label: 'General', key: 'hrm-general', children: [
            { label: 'Official Document', path: '/hrm/official-document' },
            { label: 'Payroll Tax Structure', path: '/hrm/payroll-tax-structure' }
          ]
        },
        {
          label: 'Employee Management', key: 'hrm-employee-mgmt', children: [
            { label: 'Employee Profile', path: '/hrm/employee-profile' },
            { label: 'Contract', path: '/hrm/employee-contract' },
            { label: 'Custody', path: '/hrm/employee-custody' },
            { label: 'Notices', path: '/hrm/employee-notices' },
            { label: 'Employee Expenses', path: '/hrm/employee-expenses' },
            { label: 'Employee Dependents', path: '/hrm/employee-dependents' },
            { label: 'Resignation / Termination', path: '/hrm/employee-resignation' },
            { label: 'Employee Transfer', path: '/hrm/employee-transfer' },
            { label: 'Employee Promotion', path: '/hrm/employee-promotion' }
          ]
        },
        {
          label: 'Payroll', key: 'hrm-payroll', children: [
            { label: 'Allowance Structure', path: '/hrm/allowance-structure' },
            { label: 'Deduction Structure', path: '/hrm/deduction-structure' },
            { label: 'Payroll Generator', path: '/hrm/payroll-generator' },
            { label: 'Payroll Details', path: '/hrm/payroll-details' },
            { label: 'Loan Management', path: '/hrm/loan-management' },
            { label: 'Advance Salary', path: '/hrm/advance-salary' }
          ]
        },
        {
          label: 'Service Setup', key: 'hrm-service-setup', children: [
            { label: 'EOSB', path: '/hrm/eosb' },
            { label: 'EOSB Structure', path: '/hrm/eosb-structure' }
          ]
        },
        {
          label: 'Payroll Setup', key: 'hrm-payroll-setup', children: [
            { label: 'Pay Group', path: '/hrm/pay-group' },
            { label: 'Allowances', path: '/hrm/allowances' },
            { label: 'Deduction Category', path: '/hrm/deduction-category' },
            { label: 'Payroll Posting Setup', path: '/hrm/payroll-posting-setup' },
            { label: 'Payroll Settings', path: '/hrm/payroll-settings' }
          ]
        },
        {
          label: 'Service Transactions', key: 'hrm-service-transactions', children: [
            { label: 'Withdrawl', path: '/hrm/withdrawl' },
            { label: 'Attendance', path: '/hrm/attendance' },
            { label: 'Assets Entry', path: '/hrm/assets-entry' }
          ]
        },
        {
          label: 'Service Letter', key: 'hrm-service-letter', children: [
            { label: 'Letterhead', path: '/hrm/letterhead' },
            { label: 'Employee Letter', path: '/hrm/employee-letter' }
          ]
        },
        {
          label: 'Leave Management', key: 'hrm-leave-mgmt', children: [
            { label: 'Leave Type', path: '/hrm/leave-type' },
            { label: 'Vacation Structure', path: '/hrm/vacation-structure' },
            { label: 'Leave', path: '/hrm/leave' },
            { label: 'Leave Application', path: '/hrm/leave-apply' },
            { label: 'Weekend Structure', path: '/hrm/weekend-structure' },
            { label: 'Public Holidays', path: '/hrm/public-holidays' }
          ]
        },
        {
          label: 'Directory', key: 'hrm-directory', children: [
            { label: 'Employee Directory', path: '/hrm/employee-directory' }
          ]
        },
        {
          label: 'Task Management', key: 'hrm-task-mgmt', children: [
            { label: 'Task Types', path: '/hrm/task-types' },
            { label: 'Task Status', path: '/hrm/task-status' },
            { label: 'Employee Tasks', path: '/hrm/employee-tasks' },
            { label: 'Projects', path: '/hrm/projects' },
            { label: 'Complete Task', path: '/hrm/complete-task' }
          ]
        },
        {
          label: 'Settings', key: 'hrm-settings', children: [
            { label: 'Code Formats', path: '/hrm/code-formats' },
            { label: 'Expiry Alerts', path: '/hrm/alerts' }
          ]
        }
      ]
    },
    {
      label: 'Approvals',
      key: 'approvals',
      icon: 'M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
      iconColor: 'text-brand',
      iconBg: 'bg-brand/10',
      groups: [
        {
          label: 'Workflow', key: 'approvals-workflow', children: [
            { label: 'My Approvals', path: '/approvals' }
          ]
        }
      ]
    },
    {
      // Rec-3 · ESS / MSS Self-Service. The ESS group shows only for logins bound to an
      // employee; the MSS group shows only for manager logins (see gateOk / visibleSections).
      label: 'Self Service',
      key: 'self-service',
      icon: 'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z',
      iconColor: 'text-success',
      iconBg: 'bg-success/10',
      groups: [
        {
          label: 'Employee (ESS)', key: 'self-service-ess', gate: 'employee', children: [
            { label: 'My Profile', path: '/self-service/my-profile' },
            { label: 'My Payslips', path: '/self-service/my-payslips' },
            { label: 'My Leave', path: '/self-service/my-leave' },
            { label: 'My Attendance', path: '/self-service/my-attendance' },
            { label: 'My Loans & Advances', path: '/self-service/my-loans' },
            { label: 'My Tasks', path: '/self-service/my-tasks' }
          ]
        },
        {
          label: 'Manager (MSS)', key: 'self-service-mss', gate: 'manager', children: [
            { label: 'My Team', path: '/self-service/team' },
            { label: 'Team Leave', path: '/self-service/team-leave' },
            { label: 'Team Payroll', path: '/self-service/team-payroll' },
            { label: 'Team Approvals', path: '/approvals' }
          ]
        }
      ]
    },
    {
      label: 'HRM Reports',
      key: 'hrm-reports',
      icon: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z',
      iconColor: 'text-brand',
      iconBg: 'bg-brand/10',
      groups: [
        {
          label: 'Employee', key: 'hrm-reports-employee', children: [
            { label: 'Employee Directory', path: '/hrm-reports/employee-directory' },
            { label: 'Contract Report', path: '/hrm-reports/contract' },
            { label: 'Custody Report', path: '/hrm-reports/custody' },
            { label: 'Employee Document Expiry', path: '/hrm-reports/document-expiry' }
          ]
        },
        {
          label: 'Payroll', key: 'hrm-reports-payroll', children: [
            { label: 'Payroll Register', path: '/hrm-reports/payroll-register' },
            { label: 'Payroll Allowance Report', path: '/hrm-reports/payroll-allowance' },
            { label: 'Payroll Deduction Report', path: '/hrm-reports/payroll-deduction' },
            { label: 'Allowance Structure Report', path: '/hrm-reports/allowance-structure' },
            { label: 'Deduction Structure Report', path: '/hrm-reports/deduction-structure' },
            { label: 'Salary Tax Report', path: '/hrm-reports/salary-tax' },
            { label: 'Loan Report', path: '/hrm-reports/loan' },
            { label: 'Advance Salary Report', path: '/hrm-reports/advance' }
          ]
        },
        {
          label: 'Attendance & Leave', key: 'hrm-reports-attendance-leave', children: [
            { label: 'Attendance Report', path: '/hrm-reports/attendance' },
            { label: 'Leave Report', path: '/hrm-reports/leave' },
            { label: 'Leave Balance Report', path: '/hrm-reports/leave-balance' }
          ]
        },
        {
          label: 'Service & Assets', key: 'hrm-reports-service-assets', children: [
            { label: 'Assets Report', path: '/hrm-reports/assets' },
            { label: 'EOSB / Settlement Report', path: '/hrm-reports/eosb' }
          ]
        }
      ]
    },
    {
      label: 'CRM',
      key: 'crm',
      icon: 'M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6Z M13.5 10.5H21A7.5 7.5 0 0 0 13.5 3v7.5Z',
      iconColor: 'text-success',
      iconBg: 'bg-success/10',
      groups: [
        {
          label: 'Configurations', key: 'crm-configurations', children: [
            { label: 'Companies', path: '/crm/companies' },
            { label: 'Contacts', path: '/crm/contacts' },
            { label: 'Pipeline Stages', path: '/crm/stages' },
            { label: 'Activity Types', path: '/crm/activity-type' }
          ]
        },
        {
          label: 'Leads', key: 'crm-leads', children: [
            { label: 'Leads Pipeline', path: '/crm/leads' },
            { label: 'Activity Calendar', path: '/crm/calendar' }
          ]
        },
        {
          label: 'CRM Reports', key: 'crm-reports', children: [
            { label: 'Leads Report', path: '/crm/reports/leads' },
            { label: 'Activity Report', path: '/crm/reports/activity' }
          ]
        }
      ]
    },
    {
      label: 'Sales Performance',
      key: 'sales-performance',
      icon: 'M2.25 18 9 11.25l4.306 4.307a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.28m5.94 2.28-2.28 5.94',
      iconColor: 'text-success',
      iconBg: 'bg-success/10',
      groups: [
        {
          label: 'Performance', key: 'sp-performance', children: [
            { label: 'Performance Dashboard', path: '/sales-performance/dashboard' }
          ]
        },
        {
          label: 'Setup', key: 'sp-setup', children: [
            { label: 'Sales Teams', path: '/sales-performance/teams' },
            { label: 'Quota Allocation', path: '/sales-performance/quotas' }
          ]
        }
      ]
    },
    {
      // Customer Support Center (CSC) — placed below CRM, above POS (Sir Rashid, 2026-08-06).
      label: 'Customer Support Center',
      key: 'csc',
      icon: 'M16.712 4.33a9.027 9.027 0 011.652 1.306c.51.51.944 1.064 1.306 1.652M16.712 4.33l-3.448 4.138m3.448-4.138a9.014 9.014 0 00-9.424 0M19.67 7.288l-4.138 3.448m4.138-3.448a9.014 9.014 0 010 9.424m-4.138-5.976a3.736 3.736 0 00-.88-1.388 3.737 3.737 0 00-1.388-.88m2.268 2.268a3.765 3.765 0 010 2.528m-2.268-4.796a3.765 3.765 0 00-2.528 0m4.796 4.796c-.181.506-.475.982-.88 1.388a3.736 3.736 0 01-1.388.88m2.268-2.268l4.138 3.448m0 0a9.014 9.014 0 01-9.424 0m5.976-4.138a3.765 3.765 0 01-2.528 0m0 0a3.736 3.736 0 01-1.388-.88 3.737 3.737 0 01-.88-1.388m2.268 2.268L7.288 19.67m0 0a9.024 9.024 0 01-1.652-1.306 9.027 9.027 0 01-1.306-1.652m2.958 2.958l3.448-4.138M4.33 16.712a9.014 9.014 0 010-9.424m0 9.424l4.138-3.448M4.33 7.288l4.138 3.448',
      iconColor: 'text-info',
      iconBg: 'bg-info/10',
      groups: [
        {
          label: 'Support Desk', key: 'csc-support-desk', children: [
            { label: 'Support Dashboard', path: '/csc/dashboard' },
            { label: 'Tickets', path: '/csc/tickets' }
          ]
        },
        {
          label: 'Configuration', key: 'csc-configuration', children: [
            { label: 'Ticket Categories', path: '/csc/categories' },
            { label: 'Ticket Priorities', path: '/csc/priorities' },
            { label: 'Ticket Statuses', path: '/csc/statuses' },
            { label: 'Support Agents', path: '/csc/agents' },
            { label: 'SLA Policies', path: '/csc/sla-policies' },
            { label: 'Canned Responses', path: '/csc/canned-responses' }
          ]
        },
        {
          label: 'Knowledge Base', key: 'csc-knowledge-base', children: [
            { label: 'KB Articles', path: '/csc/kb-articles' }
          ]
        },
        {
          label: 'CSC Reports', key: 'csc-reports', children: [
            { label: 'Tickets Report', path: '/csc/reports/tickets' },
            { label: 'Agent Performance', path: '/csc/reports/agent-performance' },
            { label: 'SLA Compliance', path: '/csc/reports/sla-compliance' }
          ]
        }
      ]
    },
    {
      label: 'Logix AI POS',
      key: 'pos',
      icon: 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z',
      iconColor: 'text-brand',
      iconBg: 'bg-brand/10',
      groups: [
        {
          label: 'Point of Sale', key: 'pos-sale', children: [
            { label: 'Salesman Booking', path: '/pos/booking' },
            { label: 'Counter Queue', path: '/pos/counter-queue' },
            { label: 'Cashier', path: '/pos/cashier' }
          ]
        },
        {
          label: 'Core', key: 'pos-core', children: [
            { label: 'Terminals / Counters', path: '/pos/counters' },
            { label: 'POS Users', path: '/pos/users' }
          ]
        },
        {
          label: 'App Settings', key: 'pos-app-settings', children: [
            { label: 'Product Families', path: '/pos/product-families' },
            { label: 'Bill Format', path: '/pos/bill-format' },
            { label: 'Weigh Scale Format', path: '/pos/weigh-scale-format' },
            { label: 'Printer Family', path: '/pos/printer-family' },
            { label: 'Naming Conventions', path: '/pos/naming-conventions' },
            { label: 'Bridge Servers', path: '/pos/bridge-servers' }
          ]
        }
      ]
    },
    {
      label: 'Online Store',
      key: 'online-store',
      icon: 'M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z',
      iconColor: 'text-success',
      iconBg: 'bg-success/10',
      groups: [
        {
          label: 'Configuration', key: 'ecomm-config', children: [
            { label: 'Stores', path: '/online-store/stores' },
            { label: 'Store Catalog', path: '/online-store/catalog' },
            { label: 'Content Pages', path: '/online-store/content' }
          ]
        },
        {
          label: 'Catalog Management', key: 'ecomm-catalog', children: [
            { label: 'Menu Setup', path: '/online-store/menu-setup' },
            { label: 'Products', path: '/online-store/products' }
          ]
        },
        {
          label: 'Sales', key: 'ecomm-sales', children: [
            { label: 'Online Orders', path: '/online-store/orders' },
            { label: 'Rider Collections', path: '/online-store/collections' }
          ]
        },
        {
          label: 'Delivery', key: 'ecomm-delivery', children: [
            { label: 'Riders', path: '/online-store/riders' },
            { label: 'Delivery Charges', path: '/online-store/delivery-charges' },
            { label: 'Shipments', path: '/online-store/shipments' }
          ]
        },
        {
          label: 'Promotions', key: 'ecomm-promotions', children: [
            { label: 'Banner Products', path: '/online-store/banners' },
            { label: 'Discounts & Promotions', path: '/online-store/discounts' }
          ]
        }
      ]
    },
    // ── Administration (User Setup, 2026-08-07) — the security-admin console. Rendered at
    // the very bottom of the nav (after a divider in the template); visible only to
    // Super Admin / CEO / Administrator (AuthService.canSeeSection treats 'administration'
    // as admin-only). This is where users, roles, permissions and departments are managed.
    {
      label: 'Administration',
      key: 'administration',
      icon: 'M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z',
      iconColor: 'text-secondary',
      iconBg: 'bg-secondary/10',
      groups: [
        {
          label: 'User & Access', key: 'administration-access', children: [
            { label: 'Users', path: '/administration/users' },
            { label: 'Roles', path: '/administration/roles' },
            { label: 'Role Permissions', path: '/configuration/security/privileges' },
            { label: 'Departments', path: '/administration/departments' }
          ]
        },
        {
          label: 'Reports', key: 'administration-reports', children: [
            { label: 'User Access Report', path: '/administration/reports/user-access' },
            { label: 'Activity Tracing', path: '/configuration/security/activity-tracing' }
          ]
        }
      ]
    },
  ];
}
